// src/lib/schema/base/collection.js

import { getLastModifiedDate, cleanObject } from '@/lib/schema';


/**
 * Creates schema for a collection/category page
 * @param {object} params - Collection parameters
 * @param {string} lang - Language code
 * @returns {object} - JSON-LD schema for collection page
 */
export function generateCollectionSchema(params, lang) {
  // Extract parameters
  const {
    pageTitle,
    pageSubtitle,
    searchParams,
    baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.yourcarpet.com',
    products = [],
    codeSetName = null,
    lastModified = null
  } = params;

  // Validate required fields
  if (!pageTitle || !lang) {
    console.warn('Missing required fields for collection schema generation');
    return null;
  }

  // Construct the full URL including search parameters
  const searchParamsString = searchParams ? new URLSearchParams(searchParams).toString() : '';
  const path = `/shop/carpets`;
  const fullPath = searchParamsString ? `${path}?${searchParamsString}` : path;
  const pageUrl = `${baseUrl}/${lang}${fullPath}`;

  // Get description from parameters or create a default one
  const description = params.description || `Shop our collection of ${pageTitle.toLowerCase()} at YourCarpet.com`;

  // Get image from parameters or use default
  const image = params.image || 'https://www.yourcarpet.com/assets/open-graph/main-og.png';

  // Use provided lastModified date or get a default one
  const modifiedDate = lastModified || getLastModifiedDate(codeSetName);

  // Create the base collection schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': pageTitle,
    'description': description,
    'url': pageUrl,
    'image': image,
    'inLanguage': lang,
    'dateModified': modifiedDate,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'YourCarpet.com',
      'url': baseUrl
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': pageUrl,
      'dateModified': modifiedDate
    },
    'about': {
      '@type': 'Product',
      'name': pageTitle || 'Rugs and Carpets',
      'category': 'Home Goods > Flooring > Carpets and Rugs'
    }
  };

  // Add subtitle as alternateName if available
  if (pageSubtitle) {
    schema.alternateName = pageSubtitle;
  }

  // Add product list if we have products
  if (products && products.length > 0) {
    // Create itemListElement from products
    const itemListElement = products.map((product, index) => {
      // Handle different product structures gracefully
      const productId = product.id || product.productID;
      const productName = product.name || product.title || (product.titles && (product.titles.standard || product.titles.checkout));
      const productUrl = `${baseUrl}/${lang}/product/${productId}` || `${baseUrl}/${lang}/shop/carpets/${productId}`;
      const productImage = (product.images && product.images[0]) ||
                         (product.mainImage && (product.mainImage.url || (product.mainImage.png && product.mainImage.png.url))) ||
                         image;
      const productPrice = product.price || (product.pricing && product.pricing.current && Object.values(product.pricing.current)[0]) || '';
      const productCurrency = product.currency || 'EUR';
      const inStock = product.inStock || product.stock > 0 || product.availability || false;

      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': {
          '@type': 'Product',
          'name': productName,
          'url': productUrl,
          'image': productImage,
          'offers': {
            '@type': 'Offer',
            'price': productPrice,
            'priceCurrency': productCurrency,
            'availability': inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
          }
        }
      };
    });

    // Add product list to schema
    schema.mainEntity = {
      '@type': 'ItemList',
      'itemListElement': itemListElement,
      'numberOfItems': itemListElement.length
    };

    // Add aggregate offer information based on product data
    schema.offers = {
      '@type': 'AggregateOffer',
      'priceCurrency': 'EUR',
      'availability': 'https://schema.org/InStock',
      'offerCount': itemListElement.length
    };

    // Calculate price range if possible
    const prices = products
      .map(p => parseFloat(p.price || (p.pricing && p.pricing.current && Object.values(p.pricing.current)[0]) || 0))
      .filter(price => !isNaN(price) && price > 0);

    if (prices.length > 0) {
      schema.offers.lowPrice = Math.min(...prices);
      schema.offers.highPrice = Math.max(...prices);
    }
  }

  // Clean the object to remove any null or undefined values
  return cleanObject(schema);
}

/**
 * Creates a schema for a filtered collection (e.g., color, material, style)
 * @param {object} params - Filter parameters
 * @param {string} lang - Language code
 * @returns {object} - JSON-LD schema for filtered collection
 */
export function generateFilteredCollectionSchema(params, lang) {
  const {
    filterType,    // 'color', 'material', 'style', etc.
    filterValue,   // The specific filter value
    products = [],
    baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.yourcarpet.com',
    lastModified = null
  } = params;

  // Validate required fields
  if (!filterType || !filterValue || !lang) {
    console.warn('Missing required fields for filtered collection schema generation');
    return null;
  }

  // Capitalize filter value for title
  const formattedFilterValue = filterValue.charAt(0).toUpperCase() + filterValue.slice(1);

  // Create page title and description based on filter type
  let pageTitle = '';
  let pageDescription = '';

  switch (filterType.toLowerCase()) {
    case 'color':
    case 'colours':
    case 'colors':
    case 'maincolors':
      pageTitle = `${formattedFilterValue} Carpets`;
      pageDescription = `Shop our collection of ${formattedFilterValue.toLowerCase()} carpets and rugs at YourCarpet.com`;
      break;

    case 'material':
    case 'materials':
    case 'weavedmaterials':
      pageTitle = `${formattedFilterValue} Carpets`;
      pageDescription = `Explore our ${formattedFilterValue.toLowerCase()} carpets and rugs, handcrafted with premium materials at YourCarpet.com`;
      break;

    case 'style':
    case 'styles':
      pageTitle = `${formattedFilterValue} Style Carpets`;
      pageDescription = `Discover our collection of ${formattedFilterValue.toLowerCase()} style carpets and rugs at YourCarpet.com`;
      break;

    case 'origin':
    case 'origins':
      pageTitle = `${formattedFilterValue} Carpets`;
      pageDescription = `Authentic ${formattedFilterValue} carpets and rugs, direct from artisan makers at YourCarpet.com`;
      break;

    default:
      pageTitle = `${formattedFilterValue} Carpets`;
      pageDescription = `Shop our collection of carpets and rugs filtered by ${filterType.toLowerCase()}: ${formattedFilterValue.toLowerCase()} at YourCarpet.com`;
  }

  // Construct search params for URL
  const searchParams = { [filterType]: filterValue };

  // Create collection schema with constructed parameters
  return generateCollectionSchema({
    pageTitle,
    description: pageDescription,
    searchParams,
    baseUrl,
    products,
    lastModified
  }, lang);
}
